import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ga4Configured, initGa4, trackPageView } from "@/lib/ga4";

const KEY = "tpb_cookie_choice";

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ga4Configured()) return undefined;
    const choice = localStorage.getItem(KEY);
    if (choice === "accepted") initGa4();
    else if (!choice) setVisible(true);
    const reopen = () => setVisible(true);
    window.addEventListener("tpb:reopen-consent", reopen);
    return () => window.removeEventListener("tpb:reopen-consent", reopen);
  }, []);

  const choose = (accepted) => {
    localStorage.setItem(KEY, accepted ? "accepted" : "declined");
    if (accepted) {
      initGa4();
      trackPageView(window.location.pathname);
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 z-[80] sm:left-6 sm:right-auto sm:max-w-md"
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-consent-banner"
        >
          <div className="rounded-lg border border-sandline bg-sand p-6 shadow-[0_24px_60px_-24px_rgba(38,33,28,0.35)]">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">A quick word on cookies</p>
            <p className="mt-2 text-sm leading-relaxed text-ink-muted">
              We'd like to count visits and see which breaks people love — anonymously, and only with your OK. No advertising trackers, ever.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <button type="button" onClick={() => choose(true)} className="btn-primary px-5 py-2.5 text-sm" data-testid="cookie-accept-btn">
                Happy with that
              </button>
              <button
                type="button"
                onClick={() => choose(false)}
                className="text-sm font-semibold text-ink-muted underline-offset-4 transition-colors duration-300 hover:text-ink hover:underline"
                data-testid="cookie-decline-btn"
              >
                No thanks
              </button>
            </div>
            <p className="mt-3 text-xs text-ink-muted">
              <Link to="/cookies" className="underline underline-offset-2 transition-colors duration-300 hover:text-primary" data-testid="cookie-policy-link">
                Read the cookie policy
              </Link>
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
